import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchLendingAPIJSON } from '@iwoca/lapi-client/lendingAPI';
import Cookies from 'js-cookie';

import { runSplitTest } from './runSplitTest';
import { SPLIT_TESTS } from './SPLIT_TESTS';
import { getSplitTestFromContext } from '../hooks/useSplitTest';
import { useGetContext } from '../lendingApi/lendingApiQueries';
import { TGetContext } from '../types';

export type TBranch = { branchName: string; testPercentage: number };
export type TAnonymousSplitTest = (typeof SPLIT_TESTS)['tests'][number];
export type TSplitTestData = {
  test_name: string;
  branch_name: string;
  created?: string;
  fraction?: number;
  random_value?: number;
};

const setProductionSplitTestCookies = (
  data: TSplitTestData,
  siteURL: string,
) => {
  Cookies.set(`iw-${data['test_name']}`, JSON.stringify(data), {
    domain: siteURL,
    expires: 7665,
  });
};

const updateSplitTestData = async (data: TSplitTestData) => {
  const cookieExists = !!Cookies.get(`iw-${data['test_name']}`);
  const isProd = window.location.hostname === 'iwoca.co.uk';

  if (cookieExists) return;

  if (isProd) {
    setProductionSplitTestCookies(data, 'https://iwoca.co.uk');
  } else {
    Cookies.set(`iw-${data['test_name']}`, JSON.stringify(data));
  }

  tracking.ampli.addedToAnonymousSplitTest({
    Branch: data.branch_name,
    'Test name': data.test_name,
    UID: Cookies.get('uid') || '',
  });

  // Add record for this user
  await fetchLendingAPIJSON('/api/split_tests/add_to_split_test/', {
    method: 'post',
    body: data,
  });
};

const getSplitTestBranch = (
  splitTest: TAnonymousSplitTest,
  context: TGetContext['data'],
): TSplitTestData => {
  const testName = splitTest.name;
  const splitTestCookie = Cookies.get(`iw-${testName}`);

  const testFromContext = getSplitTestFromContext(testName, context);

  if (testFromContext) {
    return {
      test_name: testName,
      branch_name: testFromContext[testName] ? 'INCLUDE' : 'EXCLUDE',
    };
  }

  if (splitTestCookie) {
    return JSON.parse(splitTestCookie);
  }

  const randomNumber = Math.random();
  const activeBranch = runSplitTest(splitTest, randomNumber);

  const currentDateTime = new Date();

  const splitTestData = {
    test_name: testName,
    branch_name: activeBranch.branchName,
    created: currentDateTime.toISOString(),
    fraction: activeBranch.testPercentage,
    random_value: randomNumber,
  };

  void updateSplitTestData(splitTestData);

  return splitTestData;
};

export const useAnonymousSplitTests = ({
  testFilter,
  supportSplitTests = true,
}: {
  testFilter?: TAnonymousSplitTest['name'];
  supportSplitTests?: boolean;
} = {}) => {
  const { context } = useGetContext();

  if (!supportSplitTests) return [];

  const activeTests = SPLIT_TESTS.tests.filter(
    (test) => test.testIsActive && test.customerIsActive,
  );

  const tests = activeTests.map((test) => {
    return getSplitTestBranch(test, context);
  });

  if (testFilter) return tests.filter((test) => test.test_name === testFilter);

  return tests;
};
